import type { ReactNode } from 'react';
import { Meter, StatusDot } from '@paige/ui';
import { useFormat } from '@paige/format';
import { Idle, Preparation, UseBar, type CardUse } from '../flow/FlowCardsStrip';
import type { FlowCard } from '../flow/flow-types';
import type { DeployedPart } from './pools-types';
import { buildWord, gigabytes, partDoes, partName, stateTone, stateWord } from './pools-words';

/** A pool's cards as one reading: the mean of the cards that answered asks in the last quarter hour. */
export function poolUse(cards: FlowCard[]): CardUse | null {
  const first = cards[0];
  if (!first) return null;
  const mean = (values: number[]): number => values.reduce((n, v) => n + v, 0) / cards.length;
  const busy = Math.min(1, mean(cards.map((c) => c.busy)));
  return {
    card: first.pool,
    parts: [{ ...first, busy }],
    busy,
    series: first.series.map((_, i) => Math.min(1, mean(cards.map((c) => c.series[i] ?? 0)))),
  };
}

/** How busy a pool is, drawn with the Stages and queues screen's own pieces from the same finished asks. */
function Use({ part }: { part: DeployedPart }): ReactNode {
  if (part.state === 'off') return <p className="pl-part__quiet">Switched off: none wanted.</p>;
  if (part.id === 'page-work') return <p className="pl-part__quiet">Use is not measured here.</p>;
  if (part.id === 'page-prep') return <Preparation cards={part.cards} />;
  const use = poolUse(part.cards);
  if (!use) return <Idle what="no asks in 15 min" />;
  return (
    <>
      <UseBar use={use} legend={false} />
      {part.cards.length > 1 ? (
        <p className="pl-part__quiet">Across {part.cards.length} cards.</p>
      ) : null}
    </>
  );
}

function Memory({ part }: { part: DeployedPart }): ReactNode {
  const f = useFormat();
  const used = part.memoryUsedBytes;
  const limit = part.memoryLimitBytes;
  if (used === null) return null;
  if (limit === null || limit <= 0) {
    return <p className="pl-part__quiet">Memory {f.decimal(gigabytes(used), 1)} GB</p>;
  }
  const share = used / limit;
  return (
    <div className="pl-part__memory">
      <span className="pl-part__label">Memory</span>
      <Meter
        value={Math.round(share * 100)}
        label={`${partName(part.id)} memory`}
        tone={share >= 0.9 ? 'danger' : share >= 0.75 ? 'warning' : 'accent'}
        display={`${f.decimal(gigabytes(used), 1)} of ${f.decimal(gigabytes(limit), 1)} GB`}
      />
    </div>
  );
}

/** The build it runs, when that build rolled out, and restarts since. */
function Facts({ part, now }: { part: DeployedPart; now: number }): ReactNode {
  const f = useFormat();
  return (
    <dl className="pl-part__facts">
      <div>
        <dt>Build</dt>
        <dd>
          <code title={part.build ?? undefined}>{buildWord(part.build)}</code>
        </dd>
      </div>
      <div>
        <dt>Released</dt>
        <dd title={part.releasedAt ? f.dateTime(part.releasedAt) : undefined}>
          {part.releasedAt ? f.ago(part.releasedAt, now) : 'not known'}
        </dd>
      </div>
      <div>
        <dt>Restarts since then</dt>
        <dd className={part.restarts > 0 ? 'pl-part__restarts' : undefined}>
          {f.int(part.restarts)}
        </dd>
      </div>
    </dl>
  );
}

/**
 * One pool or service: whether it is up (a dot, a word and "2 of 2 ready"), how busy (a pool) or how much
 * memory (a service, when known), the build it runs and since when, and restarts since that release.
 * `known` is false when the report is stale: the dot goes grey, because the state is as of then.
 */
export function PoolPart({
  part,
  now,
  known,
}: {
  part: DeployedPart;
  now: number;
  known: boolean;
}): ReactNode {
  const f = useFormat();
  const does = partDoes(part.id);
  const state = stateWord[part.state];
  return (
    <article className={`pl-part pl-part--${part.state}`} aria-label={partName(part.id)}>
      <header className="pl-part__head">
        <StatusDot tone={known ? stateTone[part.state] : 'neutral'} label={state} />
        <h3 className="pl-part__name">{partName(part.id)}</h3>
        <span className="pl-part__state">{state}</span>
      </header>
      {does ? <p className="pl-part__does">{does}</p> : null}
      <p className="pl-part__ready">
        <strong>{f.int(part.ready)}</strong> of {f.int(part.wanted)} ready
      </p>
      {part.kind === 'pool' ? <Use part={part} /> : <Memory part={part} />}
      <Facts part={part} now={now} />
    </article>
  );
}
